/**
 * scheduleDiff — Sprint 4.7.2
 *
 * Diff puro entre duas listas de entries de agenda. Produz estrutura
 * organizada para renderização visual (matriz Dia × Atual | Proposta).
 *
 * Cada entry tem N dias (days: number[]). Para a UI é mais útil ver
 * por dia da semana, então expandimos cada entry em "slots por dia".
 *
 * Pareamento (qual slot da proposta corresponde a qual slot atual?):
 *   Chave = (day, modo, time_signature)
 *   - modo "pulse"  → time_signature = entry.time
 *   - modo "onoff"  → time_signature = entry.on_time + "-" + entry.off_time
 *
 * Status do slot:
 *   - unchanged  → atual e proposta idênticos (campos chave + action + duration)
 *   - changed    → mesma chave, mas algum campo diferente (action/duration)
 *   - added      → existe só na proposta
 *   - removed    → existe só no atual
 *
 * NOTA: dois entries no mesmo (day, modo, time) na mesma lista são
 * tratados como o primeiro (caso degenerado — backend não deveria deixar).
 */

import { isPulseEntry } from "./ScheduleEditor.jsx";

export const DAY_NAMES_FULL = [
  "Domingo", "Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado",
];

/**
 * Modo legível para a UI (string técnica, não traduzir).
 */
export function entryMode(entry) {
  return isPulseEntry(entry) ? "pulse" : "onoff";
}

/**
 * Time signature de um slot — usado como chave de pareamento.
 */
export function timeSignature(entry) {
  if (isPulseEntry(entry)) return entry.time || "??:??";
  return `${entry.on_time || "??:??"}-${entry.off_time || "??:??"}`;
}

/**
 * Compara dois slots da MESMA chave (day, modo, time_signature).
 * Retorna 'unchanged' se idênticos no resto (action, duration, enabled),
 * 'changed' caso contrário.
 */
function compareSameKeySlot(a, b) {
  if ((a.action || "") !== (b.action || "")) return "changed";
  if ((a.enabled !== false) !== (b.enabled !== false)) return "changed";
  if (isPulseEntry(a)) {
    if ((Number(a.duration_s) || 0) !== (Number(b.duration_s) || 0)) return "changed";
  }
  return "unchanged";
}

/**
 * Expande uma lista de entries em slots por dia.
 *
 * @returns Array<{day, sig, mode, entry}>
 */
function expandToSlots(entries) {
  const out = [];
  if (!Array.isArray(entries)) return out;
  for (const e of entries) {
    if (!e) continue;
    const days = Array.isArray(e.days) ? e.days : [];
    const mode = entryMode(e);
    const sig = timeSignature(e);
    for (const d of days) {
      if (typeof d !== "number" || d < 0 || d > 6) continue;
      out.push({ day: d, sig, mode, entry: e });
    }
  }
  return out;
}

/**
 * Calcula o diff principal.
 *
 * @param current   Array<entry> — agenda atual (snapshot do device)
 * @param proposed  Array<entry> — agenda proposta (editor local)
 * @returns {
 *   byDay: [{day, dayName, rows: [{status, current?, proposed?, sig, mode}]}],
 *   summary: { added, removed, changed, unchanged },
 *   identical: bool,
 * }
 */
export function diffSchedules(current, proposed) {
  const curSlots = expandToSlots(current);
  const propSlots = expandToSlots(proposed);

  // Indexa por chave composta (day|mode|sig). Mantém só o primeiro slot
  // se houver chave duplicada (caso degenerado — não acontece em uso normal).
  const curByKey = new Map();
  for (const s of curSlots) {
    const k = `${s.day}|${s.mode}|${s.sig}`;
    if (!curByKey.has(k)) curByKey.set(k, s);
  }
  const propByKey = new Map();
  for (const s of propSlots) {
    const k = `${s.day}|${s.mode}|${s.sig}`;
    if (!propByKey.has(k)) propByKey.set(k, s);
  }

  // Junta todas as chaves
  const allKeys = new Set([...curByKey.keys(), ...propByKey.keys()]);

  // Resultado agrupado por dia
  const rowsByDay = new Map();
  let added = 0, removed = 0, changed = 0, unchanged = 0;

  for (const k of allKeys) {
    const cur = curByKey.get(k);
    const prop = propByKey.get(k);
    const day = (cur || prop).day;
    const sig = (cur || prop).sig;
    const mode = (cur || prop).mode;

    let status;
    if (cur && prop) {
      status = compareSameKeySlot(cur.entry, prop.entry);
      if (status === "changed") changed++;
      else unchanged++;
    } else if (cur && !prop) {
      status = "removed";
      removed++;
    } else {
      status = "added";
      added++;
    }

    const row = {
      status,
      current: cur ? cur.entry : null,
      proposed: prop ? prop.entry : null,
      sig,
      mode,
    };

    if (!rowsByDay.has(day)) rowsByDay.set(day, []);
    rowsByDay.get(day).push(row);
  }

  // Ordena linhas dentro do dia por time_signature (ordem cronológica
  // estável: '04:30' < '06:00' < '06:00-19:00')
  for (const rows of rowsByDay.values()) {
    rows.sort((a, b) => {
      if (a.sig < b.sig) return -1;
      if (a.sig > b.sig) return 1;
      return 0;
    });
  }

  // Ordena dias: 0..6 (Dom..Sáb).
  // SOMENTE retorna dias com pelo menos uma row.
  const byDay = [];
  for (let d = 0; d < 7; d++) {
    const rows = rowsByDay.get(d);
    if (rows && rows.length > 0) {
      byDay.push({ day: d, dayName: DAY_NAMES_FULL[d], rows });
    }
  }

  return {
    byDay,
    summary: { added, removed, changed, unchanged },
    identical: added === 0 && removed === 0 && changed === 0,
  };
}

/**
 * Compara dois objetos de schedule (com enabled/exceptions além de entries).
 * Útil para o resumo do drawer.
 */
export function diffScheduleFull(currentSchedule, proposedSchedule) {
  const a = currentSchedule || {};
  const b = proposedSchedule || {};
  const enabledChanged = (a.enabled === true) !== (b.enabled === true);
  const entriesDiff = diffSchedules(a.entries || [], b.entries || []);

  // Exceptions: diff simples por valor string (YYYY-MM-DD)
  const aEx = new Set(Array.isArray(a.exceptions) ? a.exceptions : []);
  const bEx = new Set(Array.isArray(b.exceptions) ? b.exceptions : []);
  const exceptionsAdded = [...bEx].filter((x) => !aEx.has(x));
  const exceptionsRemoved = [...aEx].filter((x) => !bEx.has(x));

  return {
    ...entriesDiff,
    enabledChanged,
    enabledFrom: a.enabled === true,
    enabledTo: b.enabled === true,
    exceptionsAdded,
    exceptionsRemoved,
    // identical = tudo igual (incluindo enabled e exceptions)
    identical:
      entriesDiff.identical &&
      !enabledChanged &&
      exceptionsAdded.length === 0 &&
      exceptionsRemoved.length === 0,
  };
}
